import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

dayjs.extend(utc);
dayjs.extend(timezone);

export function formatDate(date: string, dateFormat: string = "YYYY-MM-DD HH:mm:ss") {
  const localTimezone = dayjs.tz.guess();
  return dayjs.utc(date).tz(localTimezone).format(dateFormat);
}

export function formatDateByTimestamp(time: number, dateFormat: string = "YYYY-MM-DD HH:mm:ss") {
  const localTimezone = dayjs.tz.guess();
  return dayjs.unix(time).tz(localTimezone).format(dateFormat);
}

/**
 * Format a raw phone number to Korean phone format.
 * @param phone - Raw phone string (e.g. "01012345678", "821012345678", "010-1234-5678")
 * @param international - If true, returns "+82 10-XXXX-XXXX"; if false, returns "010-XXXX-XXXX"
 * @returns Formatted phone string or original string if pattern doesn't match
 */
export function formatPhoneNumber(phone: string, international: boolean = true): string {
  const digits = phone.replace(/\D/g, "");

  // Korean mobile: strip leading 82 if present, then rebuild with leading 0
  let local = digits;
  if (local.startsWith("82") && local.length >= 11) {
    local = "0" + local.slice(2);
  }

  // Format as Korean mobile: 01X-XXXX-XXXX
  if (local.startsWith("01") && local.length >= 10) {
    const prefix = local.slice(0, 3);
    const part1 = local.slice(3, 7);
    const part2 = local.slice(7, 11);
    if (international) {
      return `+82 ${prefix.slice(1)}-${part1}-${part2}`;
    }
    return `${prefix}-${part1}-${part2}`;
  }

  // Fallback: return as-is if it doesn't match Korean mobile pattern
  return phone;
}

export const getCategoryBadgeStyle = (category: string): string => {
  const lower = category.toLowerCase();
  if (lower.startsWith("[vertical]")) {
    return "bg-[rgba(0,122,255,0.09)] text-[#003368]";
  }
  if (lower.startsWith("[enabler]")) {
    return "bg-[rgba(255,0,128,0.0863)] text-[#66104A]";
  }
  return "bg-[rgba(255,128,19,0.11)] text-[#651F01]";
};

export const getCategoryBadgeStyleAgentDetail = (category: string): string => {
  const lower = category.toLowerCase();
  if (lower.startsWith("[vertical]")) {
    return "bg-[rgba(0,122,255,0.09)] text-[#003368]";
  }
  if (lower.startsWith("[enabler]")) {
    return "bg-[rgba(255,0,128,0.09)] text-[#66104A]";
  }
  return "bg-[rgba(255,128,19,0.11)] text-[#FF3B00]";
};

export const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string, padding?: string }> = {
  init: { label: "초기화", bg: "bg-black/[0.06]", text: "text-[#202020]" },
  in_progress: {
    label: "진행 중",
    bg: "bg-[#FFAA72]",
    text: "text-[#000000]",
    padding: "px-[14.5px]",
  },
  under_review: {
    label: "검토 중",
    bg: "bg-[#E0B244]",
    text: "text-[#000000]",
    padding: "px-[12.5px]",
  },
  completed: {
    label: "완료",
    bg: "bg-[#43946C]",
    text: "text-[#000000]",
    padding: "px-[21px]",
  },
  rejected: {
    label: "거부됨",
    bg: "bg-[#CB3A31]",
    text: "text-[#000000]",
    padding: "px-[14.5px]"
  },
};
